import { useEffect, useState } from 'react';
import type {
  FetchRecommendationsDataArgs,
  RecommendationsServingConfig,
} from '@noths/polaris-client-listings-api';
import {
  fetchRecommendationsData,
  ListingsRequestAndFilterBuilder,
  ListingsRequestTextFilterBuilder,
  RecommendationsRequestBuilder,
} from '@noths/polaris-client-listings-api';
import { getUserAttributes } from '@noths/polaris-client-user-configuration';
import { getProductIdFromProductCode, isBrowser } from '@noths/polaris-client-utils';
import type { CurrencyCode } from '@noths/polaris-dev-ts-types';

import type { ListingsCarouselProduct } from '../../../../adapters/listingsAPIToProductCarouselAdapter';
import { listingsAPIToProductCarouselAdapter } from '../../../../adapters/listingsAPIToProductCarouselAdapter';
import { Placement } from '../../../../constants/recommendations';
import { isProd } from '../../../../environment';
import { logger } from '../../../../utils/serverLogger';
import type { ListingsAPIRecommendationPlacement } from '../types';

const createListingsAPIFilter = (
  placementType: ListingsAPIRecommendationPlacement,
  partnerShortCode: string,
) => {
  const inStockFilter = new ListingsRequestTextFilterBuilder('availability', ['IN_STOCK']).build();
  const filterCollection = [inStockFilter];

  if (placementType === Placement.SameSeller2) {
    const partnerFilter = new ListingsRequestTextFilterBuilder('attributes.partner_shortcode', [
      partnerShortCode,
    ]).build();
    filterCollection.push(partnerFilter);
  }

  const andFilter = new ListingsRequestAndFilterBuilder().filters(filterCollection).build();
  return andFilter;
};

export interface UseListingsRecommendationsProps {
  currencyCode: CurrencyCode;
  partnerShortCode: string;
  placementType: ListingsAPIRecommendationPlacement;
  productCode: string;
  servingConfig: RecommendationsServingConfig;
  visitorId?: string;
}

type CreateRecommendationsParametersProps = Pick<
  UseListingsRecommendationsProps,
  | 'currencyCode'
  | 'productCode'
  | 'servingConfig'
  | 'visitorId'
  | 'partnerShortCode'
  | 'placementType'
> & {
  userId?: string;
};

const createRecommendationsParameters = ({
  currencyCode,
  partnerShortCode,
  placementType,
  productCode,
  servingConfig,
  userId,
  visitorId,
}: CreateRecommendationsParametersProps): FetchRecommendationsDataArgs => {
  const id = getProductIdFromProductCode(Number(productCode)).toString();
  const requestBuilder = new RecommendationsRequestBuilder();
  requestBuilder.currency(currencyCode);
  requestBuilder.pageSize(10);
  requestBuilder.products([{ id }]);
  const andFilter = createListingsAPIFilter(placementType, partnerShortCode);
  requestBuilder.filter(andFilter);
  const body = requestBuilder.build();

  const clientSide = isBrowser();
  return {
    body,
    clientSide,
    env: isProd() ? 'prod' : 'qa',
    servingConfig,
    timeout: clientSide ? 15000 : 5500,
    userId,
    visitorId,
  };
};

export const useListingsRecommendations = ({
  currencyCode,
  partnerShortCode,
  placementType,
  productCode,
  servingConfig,
  visitorId,
}: UseListingsRecommendationsProps) => {
  const [recommendedProducts, setRecommendedProducts] = useState<ListingsCarouselProduct[] | null>(
    null,
  );

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setRecommendedProducts(null);
        const userAttributes = await getUserAttributes();
        const fetchParams = createRecommendationsParameters({
          currencyCode,
          partnerShortCode,
          placementType,
          productCode,
          servingConfig,
          userId: userAttributes?.sub,
          visitorId,
        });

        const responseData = await fetchRecommendationsData(fetchParams);

        if ('message' in responseData) {
          setRecommendedProducts([]);
          logger.error('Error getting recommendations from listings-api:', responseData.message);
          return;
        }

        const adaptedData = listingsAPIToProductCarouselAdapter(responseData);
        setRecommendedProducts(adaptedData.products);
      } catch (error) {
        setRecommendedProducts([]);
        logger.error('Exception thrown while getting recommendations from listings-api:', error);
      }
    };

    fetchProducts();
  }, [placementType, currencyCode, servingConfig, visitorId, productCode]);

  return {
    recommendedProducts,
  };
};
